import React from "react";
import classNames from "../../classNames";

const isImageURL = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url);
};

const getFileName = (url) => {
  if (!url) return "";
  const full = url.split("/").pop();
  const name = full.split("_").pop();
  return name || full;
};

function InputFile({
  className,
  value,
  onChange,
  saveFile,
  multiple = true,
  accept,
  ...props
}) {
  const [progress, setProgress] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [uploadingName, setUploadingName] = React.useState("");
  const inputRef = React.useRef(null);
  const filesRef = React.useRef([]);

  // Normalize value to array for internal use
  const files = React.useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return [value];
  }, [value]);

  // Keep ref in sync so async callbacks always see latest files
  React.useEffect(() => {
    filesRef.current = files;
  }, [files]);

  function emitChange(updatedFiles) {
    console.log("InputFile URLs:", updatedFiles);
    filesRef.current = updatedFiles;
    if (multiple) {
      onChange(updatedFiles);
    } else {
      onChange(updatedFiles[0] || "");
    }
  }

  function uploadFile(file) {
    if (!file) return;
    setUploadingName(file.name);
    const options = {
      progress: (val) => setProgress(val * 100),
    };
    saveFile
      .execute(file, options)
      .then((result) => {
        setProgress(0);
        setUploadingName("");
        if (multiple) {
          emitChange([...filesRef.current, result.url]);
        } else {
          emitChange([result.url]);
        }
      })
      .catch((error) => {
        setProgress(0);
        setUploadingName("");
        console.log("error", error);
      });
  }

  function _onChange(e) {
    const selected = Array.from(e.target.files);
    if (multiple) {
      selected.forEach((file) => uploadFile(file));
    } else {
      uploadFile(selected[0]);
    }
    // Reset input so same file can be re-selected
    e.target.value = "";
  }

  function onDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }
  function onDragLeave(e) {
    e.preventDefault();
    setDragging(false);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (multiple) {
      dropped.forEach((file) => uploadFile(file));
    } else {
      uploadFile(dropped[0]);
    }
  }

  function handleDelete(index) {
    const updated = files.filter((_, i) => i !== index);
    emitChange(updated);
  }

  const isUploading = progress > 0;

  return (
    <div className={classNames(className)} style={{ width: "100%" }}>
      {/* Hidden native input */}
      <input
        ref={inputRef}
        type="file"
        onChange={_onChange}
        multiple={multiple}
        accept={accept || "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"}
        style={{ display: "none" }}
        {...props}
      />

      {/* Drop zone */}
      <div
        onClick={() => !isUploading && inputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          position: "relative",
          border: dragging ? "2px solid #667eea" : "2px dashed #d1d5db",
          borderRadius: "14px",
          padding: isUploading ? "24px 20px" : "28px 20px",
          textAlign: "center",
          cursor: isUploading ? "default" : "pointer",
          background: dragging
            ? "linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))"
            : "#fafbff",
          transition: "all 0.25s ease",
          overflow: "hidden",
        }}
      >
        {/* Progress bar */}
        {isUploading && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "4px",
              width: `${progress}%`,
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              borderRadius: "0 4px 4px 0",
              transition: "width 0.3s ease",
            }}
          />
        )}

        {/* Icon */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: isUploading
              ? "linear-gradient(135deg, #f59e0b, #f97316)"
              : "linear-gradient(135deg, #667eea, #764ba2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
            boxShadow: isUploading
              ? "0 4px 14px rgba(245,158,11,0.3)"
              : "0 4px 14px rgba(102,126,234,0.25)",
            transition: "all 0.25s ease",
          }}
        >
          {isUploading ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animation: "inputfile-spin 1s linear infinite" }}
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          )}
        </div>

        {/* Text */}
        {isUploading ? (
          <>
            <p
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#334155",
              }}
            >
              Uploading... {Math.round(progress)}%
            </p>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "0.78rem",
                color: "#94a3b8",
              }}
            >
              {uploadingName}
            </p>
          </>
        ) : (
          <>
            <p
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#334155",
              }}
            >
              Drop {multiple ? "files" : "a file"} here, or{" "}
              <span style={{ color: "#667eea" }}>browse</span>
            </p>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "0.78rem",
                color: "#94a3b8",
              }}
            >
              Supports images, PDF, Word, Excel & more
            </p>
          </>
        )}
      </div>

      {/* Uploaded files list */}
      {files.length > 0 && (
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {files.map((fileURL, index) => {
            const name = getFileName(fileURL);
            const isImage = isImageURL(fileURL);

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s ease",
                }}
              >
                {/* Thumbnail / Icon */}
                {isImage ? (
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <img
                      src={fileURL}
                      alt={name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                )}

                {/* File info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a
                    href={fileURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#334155",
                      textDecoration: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={name}
                  >
                    {name || `File ${index + 1}`}
                  </a>
                  <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                    {isImage ? "Image" : "Document"}
                  </span>
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "background 0.2s ease",
                    color: "#94a3b8",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#fee2e2";
                    e.currentTarget.style.color = "#ef4444";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#94a3b8";
                  }}
                  title="Remove file"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes inputfile-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default InputFile;
